/**
 * Environment-aware download utility for handling file downloads across different environments
 * including web browsers, VSCode extensions, JetBrains plugins, and mobile apps.
 */
import { useAppStore } from "../../GlobalStore/GlobalStore";
import { isMobileApp, isIde, sendMessageToIde, sendMessageToMobile } from "../misc/envNameUtils";

export interface DownloadRequest {
    content: string;
    filename: string;
    type: 'svg' | 'xlsx' | 'png' | 'txt' | 'pdf' | 'docx' | 'json';
    mimeType?: string;
}

const MOBILE_DOWNLOAD_CHUNK_SIZE = 48_000;

// Helper function to convert base64 to Blob
const base64ToBlob = (base64: string, mimeType: string): Blob => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
};

// Helper function to download blob in web browser
const downloadBlob = async (blob: Blob, filename: string): Promise<void> => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

export const downloadContent = async (request: DownloadRequest): Promise<void> => {
    const { environment, vscode } = useAppStore.getState().environmentObj;
    
    // Get appropriate MIME type
    const getMimeType = (type: string): string => {
        switch (type) {
            case 'svg': return 'image/svg+xml';
            case 'xlsx': return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            case 'png': return 'image/png';
            case 'txt': return 'text/plain';
            case 'pdf': return 'application/pdf';
            case 'docx': return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
            case 'json': return 'application/json';
            default: return 'application/octet-stream';
        }
    };

    const mimeType = request.mimeType || getMimeType(request.type);

    try {
        if (isIde(environment)) {
            // JetBrains or VSCode Plugin download
            const message = {
                functionName: 'downloadContent',
                data: [{
                    content: request.content,
                    filename: request.filename,
                    type: request.type,
                    toolCallId: Date.now().toString()
                }]
            };

            sendMessageToIde(environment, vscode, 'downloadContent', message.data);
        } else if (isMobileApp(environment)) {
            // Mobile app download. Large payloads such as generated PNGs/XLSX files
            // need to be chunked to make it through the WebView bridge reliably.
            if (request.content.length > MOBILE_DOWNLOAD_CHUNK_SIZE) {
                const requestId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
                const totalChunks = Math.ceil(request.content.length / MOBILE_DOWNLOAD_CHUNK_SIZE);

                sendMessageToMobile('DOWNLOAD_REQUEST_START', [{
                    requestId,
                    type: request.type,
                    filename: request.filename,
                    totalChunks,
                    onProgress: true
                }]);

                for (let index = 0; index < totalChunks; index++) {
                    const start = index * MOBILE_DOWNLOAD_CHUNK_SIZE;
                    const end = start + MOBILE_DOWNLOAD_CHUNK_SIZE;

                    sendMessageToMobile('DOWNLOAD_REQUEST_CHUNK', [{
                        requestId,
                        chunkIndex: index,
                        contentChunk: request.content.slice(start, end)
                    }]);
                }

                sendMessageToMobile('DOWNLOAD_REQUEST_COMPLETE', [{
                    requestId
                }]);
            } else {
                sendMessageToMobile('DOWNLOAD_REQUEST', [{
                    type: request.type,
                    content: request.content,
                    filename: request.filename,
                    onProgress: true
                }]);
            }
        } else {
            // Web browser download
            let blob: Blob;
            
            if (request.content.startsWith('data:')) {
                // Handle data URL
                const base64 = request.content.split(',')[1];
                blob = base64ToBlob(base64, mimeType);
            } else if (request.content.startsWith('http')) {
                // Handle URL - fetch and download
                const response = await fetch(request.content);
                if (!response.ok) throw new Error('Failed to fetch content');
                blob = await response.blob();
            } else {
                // Handle raw content
                blob = new Blob([request.content], { type: mimeType });
            }

            await downloadBlob(blob, request.filename);
        }
    } catch (error) {
        console.error('Download failed:', error);
        throw new Error('Failed to download content. Please try again.');
    }
};
